import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAttributeGroups } from '@/entities/attributes';
import { NestedItems } from '@/features/nested-items';
import { useServerSearch } from '@/shared/hooks';
import { AttributeGroupValue, toAttributeGroupValue } from './adapters';
import { AttributeFormValues } from './validation';

/** Сколько групп показывать в окне: список набирается поиском по всему набору, а не листается */
const GROUPS_PAGE_LIMIT = 50;

const getGroupCode = (group: AttributeGroupValue) => group.code;
const getGroupName = (group: AttributeGroupValue) => group.name;
const getGroupDescription = (group: AttributeGroupValue) => group.description;

type Props = {
  disabled?: boolean;
};

/**
 * Группы атрибута: строки состава из данных карточки, окно выбора открывается с группами атрибута
 * отмеченными и заменяет ими состав. Группа ищется по всему набору на сервере.
 */
export const AttributeGroupsField: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['attributes', 'attributeGroups', 'common']);
  const { setValue, watch } = useFormContext<AttributeFormValues>();
  const currentGroups = watch('attributeGroups') || [];

  // Поиск идёт по всему набору групп: группа за пределами первой порции иначе недостижима
  const search = useServerSearch();
  const { data, isLoading } = useAttributeGroups({ search: search.applied || undefined, limit: GROUPS_PAGE_LIMIT });

  const handleChange = (groups: AttributeGroupValue[]) => {
    // Состав групп — такое же изменение карточки, как правка её поля: без `shouldDirty` кнопка сохранения остаётся неактивной
    setValue('attributeGroups', groups.map(toAttributeGroupValue), { shouldDirty: true });
  };

  return (
    <NestedItems<AttributeGroupValue>
      labels={{
        title: t('attributes:attributeGroups'),
        addButton: t('attributeGroups:addGroup'),
        removeButton: t('attributeGroups:removeGroupFromAttribute'),
        emptyState: t('attributeGroups:noGroupsInAttribute'),
        dialogTitle: t('attributeGroups:addGroup'),
        searchPlaceholder: t('common:search'),
        noItemsFound: t('attributeGroups:noGroupsFound'),
      }}
      value={currentGroups}
      onChange={handleChange}
      allItems={data?.attributeGroups ?? []}
      total={data?.total}
      isLoading={isLoading}
      getItemId={getGroupCode}
      renderTitle={getGroupName}
      renderHint={getGroupCode}
      renderDescription={getGroupDescription}
      serverSearch={search}
      disabled={disabled}
    />
  );
};
